import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Box, Button, Card, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import {
  CompleteOrganizationOwnerInvitation,
  GetOrganizationOwnerInvitationStatus
} from 'Apollo';
import ContextualInfo from 'Components/ContextualInfo/ContextualInfo';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { PageError } from 'Page/PageError';
import { useCallback, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { PrivateRoutes } from 'Router/Routes';
import GroupSrc from 'Theme/assets/images/group.svg';

type Props = {
  token: string | null;
};

type StatusData = {
  organizationOwnerInvitationStatus: {
    isValid: boolean;
    isExpired: boolean;
    accountLabel: string | null;
    accountCode: string | null;
    notifierFirstName: string | null;
    notifierLastName: string | null;
  };
};

const BoldFormatter = (children: React.ReactNode) => <strong>{children}</strong>;

const OrganizationOwnerInvitation = ({ token }: Props) => {
  const ts = useTranslate();
  const history = useHistory();
  const { onError, onSuccess } = useNotification();
  const [completing, setCompleting] = useState(false);
  const { data, loading, error } = useQuery<StatusData>(
    GetOrganizationOwnerInvitationStatus,
    {
      variables: { token: token ?? '' },
      skip: !token,
      fetchPolicy: 'network-only'
    }
  );

  const [completeMutation] = useMutation(CompleteOrganizationOwnerInvitation);

  const onCancel = useCallback(() => {
    history.push('/');
  }, [history]);

  const onConfirm = useCallback(async () => {
    if (!token) {
      return;
    }
    setCompleting(true);
    try {
      await completeMutation({ variables: { token } });
      onSuccess('page.organizationOwnerInvitation.success');
      history.push(
        `${generatePath(PrivateRoutes.organizations.path, { accountCode: 'all' })}?token=${encodeURIComponent(token)}`
      );
    } catch (e) {
      onError(e as ApolloError);
    } finally {
      setCompleting(false);
    }
  }, [completeMutation, history, onError, onSuccess, token]);

  if (!token) {
    return (
      <PageError
        title={ts('page.organizationOwnerInvitation.missingTokenTitle')}
        textPrimary={ts('page.organizationOwnerInvitation.missingToken')}
      />
    );
  }

  if (loading) {
    return <LinearProgress color="secondary" />;
  }

  if (error || !data) {
    return (
      <PageError
        title={ts('error.somethingHappened')}
        textPrimary={ts('page.organizationOwnerInvitation.loadError')}
      />
    );
  }

  const status = data.organizationOwnerInvitationStatus;

  if (status.isExpired) {
    return (
      <PageError
        title={ts('page.organizationOwnerInvitation.expiredTitle')}
        textPrimary={ts('page.organizationOwnerInvitation.expired')}
      />
    );
  }

  if (!status.isValid) {
    return (
      <PageError
        title={ts('page.organizationOwnerInvitation.invalidTitle')}
        textPrimary={ts('page.organizationOwnerInvitation.invalidToken')}
      />
    );
  }

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: '100vh' }}
    >
      <Grid item>
        <Card>
          <Stack direction="row" justifyContent="flex-start" sx={{ p: 2 }} spacing={2}>
            <SecurityIcon fontSize="large" color="primary" />
            <Typography variant="h4" paragraph fontWeight="bold" sx={{ p: 0.5 }}>
              {ts('page.organizationOwnerInvitation.title')}
            </Typography>
            <ContextualInfo
              content={ts('page.organizationOwnerInvitation.info')}
            />
          </Stack>
          <img src={GroupSrc} alt="group" style={{ display: 'block', margin: '0 auto' }} />
          <Box py={2} px={12} maxWidth="md">
            <Typography variant="h5" paragraph textAlign="center" fontWeight="bold">
              {ts('page.organizationOwnerInvitation.subTitle')}
            </Typography>
            <Typography variant="body1" paragraph textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
              {ts('page.organizationOwnerInvitation.introValid', {
                notifier: `${status.notifierFirstName ?? ''} ${status.notifierLastName ?? ''}`,
                accountLabel: status.accountLabel ?? '',
                b: BoldFormatter
              })}
            </Typography>
            <Box pt={2} display="flex" justifyContent="center" gap={3}>
              <Button onClick={onCancel} color="inherit">
                {ts('common.action.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={completing}
                onClick={onConfirm}
              >
                {ts('common.action.confirm')}
              </Button>
            </Box>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OrganizationOwnerInvitation;
